import type { DropLabel } from '@/features/campaign-texts/components/PerDropGroupSelector';

import type { CountryGenResult } from './multiCountryBulkEdit.types';

export interface PerDropRow {
  di: number;
  title: string;
  groupKey: string;
  texts: string[];
  /** English lines aligned with `texts` for this drop. */
  translations?: string[];
  senders: string[];
}

export function buildPerDropRows(result: CountryGenResult, dropLabels?: DropLabel[]): PerDropRow[] {
  const tb = result.textBatches;
  const trb = result.translationBatches;
  const sb = result.senderBatches;
  const gIds = result.groupIdsPerDrop;
  const n = Math.max(tb?.length ?? 0, sb?.length ?? 0);
  if (n === 0) {
    if (!result.texts.length && !result.senders.length) return [];
    const g0 = gIds?.[0];
    return [{
      di: 0,
      title: dropLabels?.[0]?.label ?? 'Drop 1',
      groupKey: g0 && g0 !== 'master' ? g0 : '__flat__',
      texts: result.texts,
      translations: result.translations,
      senders: result.senders,
    }];
  }
  const flatSenders = result.senders ?? [];
  const rows: PerDropRow[] = [];
  for (let di = 0; di < n; di++) {
    const gid = gIds?.[di];
    const batched = sb?.[di];
    const sendersForRow =
      batched !== undefined && batched.length > 0 ? batched : flatSenders.length > 0 ? flatSenders : [];
    rows.push({
      di,
      title: dropLabels?.[di]?.label ?? `Drop ${di + 1}`,
      groupKey: gid && gid !== 'master' ? gid : '__flat__',
      texts: tb?.[di] ?? [],
      translations: trb?.[di],
      senders: sendersForRow,
    });
  }
  return rows;
}

export function groupRowsByPrompt(rows: PerDropRow[]): { key: string; rows: PerDropRow[] }[] {
  const order: string[] = [];
  const map = new Map<string, PerDropRow[]>();
  for (const r of rows) {
    if (!map.has(r.groupKey)) {
      map.set(r.groupKey, []);
      order.push(r.groupKey);
    }
    map.get(r.groupKey)!.push(r);
  }
  return order.map((key) => ({ key, rows: map.get(key)! }));
}
